import { useDebounce } from '@hooks/debounce';
import { CommonUtility } from '@utility/common';
import { DateFormat, DateUtility } from '@utility/date';
import { boxOnLineWidth, colors } from '@utility/sales-forecast';
import { Lightning, Sun } from '@phosphor-icons/react';
import { cn } from '@skupreme/shared/ui';
import { type ReactNode, useEffect, useMemo, useRef, useState } from 'react';

/** Its fill and text colours are the event series' chart colours. */
function LineLabel({
  right,
  seasonal,
  children,
  onClick,
  onMouseEnter,
  onMouseLeave,
}: {
  right: boolean;
  seasonal: boolean;
  children: ReactNode;
  onClick: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}) {
  const palette = seasonal ? colors.seasonalSpike : colors.lightningDeal;
  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents lint/a11y/noStaticElementInteractions: a pointer-only marker inside the chart's SVG, as before
    <div
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cn(
        'sk:flex sk:h-7 sk:w-full sk:cursor-pointer sk:justify-between sk:gap-[5px] sk:p-[5px] sk:text-xs',
        'sk:[&>div]:flex sk:[&>div]:gap-2',
        'sk:[&_.event-title]:w-20 sk:[&_.event-title]:overflow-hidden sk:[&_.event-title]:text-ellipsis sk:[&_.event-title]:whitespace-nowrap',
        right ? 'sk:rounded-r-[5px]' : 'sk:rounded-l-[5px]',
      )}
      style={{ color: palette.font, background: palette.background }}
    >
      {children}
    </div>
  );
}

/**
 * Renders an event line within a chart, displaying event details and interactions.
 * @param {Object} [props.data] - The data object for the event. Default - `null`
 * @param {string} [props.eventId] - The ID of the current event. Default - `''`
 * @param {boolean} [props.isSeasonal] - Specifies if the event is seasonal. Default - `false`
 * @param {Function} [props.setEventId] - Function to set the event ID. Default - `() => {}`
 * @param {Function} [props.viewBox] - View Box Data Provided By Chart
 * @returns {JSX.Element} - React component rendering event details and interactions.
 */
function SeasonalityChartEventLine(props) {
  const {
    data = null,
    eventId = '',
    isSeasonal = false,
    setEventId = () => {},
    viewBox = {},
  } = props;

  const [visible, setVisible] = useState(false);
  const innerWrapRef = useRef<HTMLDivElement>(null);

  const isSelected = eventId === data._id;
  const isVisible = visible;
  const finalVisible = useDebounce(isVisible || isSelected, 50);

  useEffect(() => {
    const tooltip = document.querySelector('.recharts-wrapper');
    const eventArea = document.querySelector(`#event-${data._id}`);

    if (tooltip) {
      if (finalVisible) {
        tooltip.classList.add('event-hover'); // To Show Event Details Header
      } else {
        tooltip.classList.remove('event-hover'); // To Close Event Details Header
      }
    }

    // @ts-expect-error - TS2339: Property 'style' does not exist on type 'Element'.
    if (eventArea) eventArea.style.opacity = finalVisible ? '1' : '0'; // To Toggle Event Section In Chart
  }, [finalVisible]);

  const Icon = isSeasonal ? Sun : Lightning;
  const RenderIcon = (
    <Icon size={16} className="sk:fill-primary" strokeWidth={2} />
  );

  const LabelContent =
    isVisible && CommonUtility.isValidObject(data) ? (
      <>
        <div>
          {RenderIcon}
          <span className="event-title">{data?.name || ''}</span>
        </div>
        <div>
          {data?.percentage !== undefined && (
            <span>
              {CommonUtility.numberWithCommas((data?.percentage ?? 0) - 100)}%
            </span>
          )}
          {data?.units !== undefined && (
            <span>{CommonUtility.numberWithCommas(data?.units)} Units</span>
          )}
          <span>
            {[data.from, data.to]
              .map(d => DateUtility.formatDate(d, DateFormat.localeMonthDate))
              .join(' - ')}
          </span>
        </div>
      </>
    ) : (
      RenderIcon
    );

  const isRight = useMemo(() => {
    const wrapperWidth =
      document.querySelector('.recharts-surface')?.clientWidth || 450;
    const maxXPosition = viewBox?.x + 250;

    return !(maxXPosition > wrapperWidth);
  }, [isVisible, viewBox?.x]);

  const objectWidth = boxOnLineWidth + (isVisible ? 250 : 0);

  const objectX = viewBox.x - (isRight ? 1 : objectWidth);

  return (
    <g className={isVisible ? 'visible' : ''}>
      {/* This foreignObject represents a container for the LineLabel */}
      <foreignObject
        height={boxOnLineWidth}
        // @ts-expect-error - TS2322: Type 'RefObject<unknown>' is not assignable to type 'Ref<SVGForeignObjectElement...
        ref={innerWrapRef}
        width={objectWidth}
        x={objectX}
        y={viewBox.y - boxOnLineWidth}
      >
        {/* LineLabel component for displaying event details */}
        <LineLabel
          onClick={() => setEventId(data._id)}
          onMouseEnter={() => setVisible(true)}
          onMouseLeave={() => setVisible(false)}
          right={isRight}
          seasonal={isSeasonal}
        >
          {/* Content inside the LineLabel component */}
          {LabelContent}
        </LineLabel>
      </foreignObject>
    </g>
  );
}

export default SeasonalityChartEventLine;
